import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { TradeType } from '../page';
import { getCityTierRates, type TradeType as TierTradeType } from '../../../config/providerTierRates';
import { useToast } from '../../../hooks/useToast';

interface ProviderDetailsFormProps {
  selectedTrade: string | null;
  onSubmit: (data: any) => void;
  onBack: () => void;
  initialData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
}

export default function ProviderDetailsForm({ selectedTrade, onSubmit, onBack, initialData }: ProviderDetailsFormProps) {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    password: '',
    confirmPassword: '',
    postcode: '',
    profilePhoto: null as File | null,
  });

  const [isVerifying, setIsVerifying] = useState(false);
  const [postcodeVerified, setPostcodeVerified] = useState(false);
  const [detectedCity, setDetectedCity] = useState<string>('');
  const [tierInfo, setTierInfo] = useState<any>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Map selected trade to API trade_type
  const mapTradeToType = (trade: string): string => {
    // Trade IDs are already in the correct format from TradeSelection component
    // Just return them directly, or map if they're in a different format
    const tradeMap: { [key: string]: string } = {
      'handyman': 'handyman',
      'plumbing': 'plumbing',
      'electrical': 'electrical',
      'cleaning': 'cleaning',
      'transportation': 'handyman',
      'waste': 'handyman',
      // Legacy mappings in case full names are used
      'Handyman Services': 'handyman',
      'Plumbing': 'plumbing',
      'Plumbing Services': 'plumbing',
      'Electrical': 'electrical',
      'Electrical Services': 'electrical',
      'Cleaning': 'cleaning',
      'Cleaning Services': 'cleaning',
      'Gardening': 'handyman',
      'Painting & Decorating': 'handyman',
      'Carpentry': 'handyman',
      'Locksmith': 'handyman',
      'Appliance Repair': 'electrical',
      'HVAC': 'handyman',
      'Roofing': 'handyman',
      'Flooring': 'handyman',
      'Tiling': 'handyman',
      'Plastering': 'handyman',
      'Bricklaying': 'handyman',
      'Waste Removal': 'handyman',
      'Man & Van': 'handyman',
      'Furniture Assembly': 'handyman',
    };
    
    return tradeMap[trade] || trade.toLowerCase();
  };

  const handleVerifyPostcode = async () => {
    if (!formData.postcode.trim()) {
      showToast('Please enter a postcode', 'error');
      return;
    }

    // Check if trade is selected before verifying
    if (!selectedTrade) {
      showToast('Please select a trade first', 'error');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/validate-postcode`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ postcode: formData.postcode }),
        }
      );

      const result = await response.json();

      if (result.valid) {
        setPostcodeVerified(true);
        setDetectedCity(result.city);
        
        // Get tier information for this city and trade
        try {
          const tierData = await getTierInfoForCity(result.city, selectedTrade);
          if (tierData) {
            setTierInfo(tierData);
          }
        } catch (tierError) {
          console.error('Error fetching tier info:', tierError);
          // Don't show error to user, tier info is optional
        }
        
        showToast(`Postcode verified! You're in ${result.city}`, 'success');
      } else {
        showToast('Invalid postcode or outside service area', 'error');
      }
    } catch (error) {
      console.error('Postcode verification error:', error);
      showToast('Failed to verify postcode', 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  const getTierInfoForCity = async (cityName: string, trade: string | null) => {
    try {
      if (!trade) {
        console.error('No trade selected');
        return null;
      }
      
      console.log('Fetching tier info for:', cityName, trade);
      
      // Map the trade to the correct API format
      const tradeType = mapTradeToType(trade);
      console.log('Mapped trade type:', tradeType);

      // Only call the API if we have a valid trade type
      if (!tradeType || !['handyman', 'plumbing', 'electrical', 'cleaning'].includes(tradeType)) {
        console.error('Invalid trade type:', tradeType);
        return null;
      }
      
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-provider-tier-rates?trade_type=${tradeType}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          }
        }
      );

      if (!response.ok) {
        console.error('API response not OK:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        return null;
      }

      const result = await response.json();
      console.log('Tier rates response:', result);
      
      if (!result.success || !result.data || !result.data.cities) {
        console.error('Invalid tier data received:', result);
        return null;
      }

      // Find the city in the response
      const citySlug = cityName.toLowerCase().replace(/\s+/g, '-');
      const cityData = result.data.cities.find((c: any) => 
        c.citySlug === citySlug || c.cityName.toLowerCase() === cityName.toLowerCase()
      );

      if (!cityData) {
        console.error('City not found in tier data:', cityName);
        return null;
      }

      console.log('Found city data:', cityData);
      return cityData;
    } catch (error) {
      console.error('Error fetching tier info:', error);
      return null;
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        showToast('Photo must be less than 5MB', 'error');
        return;
      }
      setFormData({ ...formData, profilePhoto: file });
    }
  };

  const validatePassword = () => {
    if (formData.password.length < 8) {
      showToast('Password must be at least 8 characters long', 'error');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      showToast('Passwords do not match', 'error');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password
    if (!validatePassword()) {
      return;
    }

    if (!postcodeVerified) {
      showToast('Please verify your postcode first', 'error');
      return;
    }

    if (!formData.profilePhoto) {
      showToast('Please upload a profile photo', 'error');
      return;
    }

    setIsSubmitting(true);
    setUploadingPhoto(true);

    // Upload profile photo to Supabase Storage
    let photoUrl = '';
    if (formData.profilePhoto) {
      try {
        showToast('Uploading profile photo...', 'info');
        
        const fileExt = formData.profilePhoto.name.split('.').pop();
        const fileName = `${Date.now()}-${formData.email.replace(/[^a-zA-Z0-9]/g, '_')}.${fileExt}`;
        const filePath = `profile-photos/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('provider-documents')
          .upload(filePath, formData.profilePhoto);

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('provider-documents')
          .getPublicUrl(filePath);

        photoUrl = publicUrl;
        
        console.log('✅ Photo uploaded successfully:', photoUrl);
        showToast('Profile photo uploaded successfully!', 'success');
        
        // Small delay to show success message
        await new Promise(resolve => setTimeout(resolve, 800));
        
      } catch (error) {
        console.error('Photo upload error:', error);
        showToast('Failed to upload profile photo. Please try again.', 'error');
        setIsSubmitting(false);
        setUploadingPhoto(false);
        return;
      }
    }

    setUploadingPhoto(false);

    // Prepare complete application data
    const completeData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      password: formData.password,
      postcode: formData.postcode,
      city: detectedCity,
      selectedTrade: selectedTrade,
      profilePhotoUrl: photoUrl,
      selfieUrl: photoUrl,
      serviceCities: [detectedCity], // Add service cities
    };

    // Save to sessionStorage for payment page
    try {
      sessionStorage.setItem('provider_application_data', JSON.stringify(completeData));
      console.log('✅ Application data saved to sessionStorage:', completeData);
    } catch (err) {
      console.error('Failed to save to sessionStorage:', err);
    }

    // Call onSubmit to update parent state
    onSubmit(completeData);
    
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              required
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              required
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Create Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-10"
                placeholder="Min. 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent pr-10"
                placeholder="Re-enter password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className={`${showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Postcode Verification */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Location</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Postcode *
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={formData.postcode}
                onChange={(e) => setFormData({ ...formData, postcode: e.target.value.toUpperCase() })}
                placeholder="e.g., SW1A 1AA"
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                disabled={postcodeVerified}
              />
              <button
                type="button"
                onClick={handleVerifyPostcode}
                disabled={isVerifying || postcodeVerified || !formData.postcode}
                className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                {isVerifying ? 'Verifying...' : postcodeVerified ? 'Verified' : 'Verify'}
              </button>
            </div>
            {postcodeVerified && detectedCity && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-2">
                <i className="ri-map-pin-line"></i>
                Service area: {detectedCity}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tier Information - Show after postcode verification */}
      {postcodeVerified && tierInfo && (
        <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-lg border-2 border-teal-200 p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-price-tag-3-line text-white text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {tierInfo.tierLabel} - {tierInfo.cityName}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Based on your location, here are your earning rates for {selectedTrade?.toLowerCase()}:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-white rounded-lg p-4 border border-teal-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-sun-line text-teal-600"></i>
                    <div className="text-xs text-gray-500">Normal Hours</div>
                  </div>
                  <div className="text-xs text-gray-500 mb-1">6:00 AM - 6:00 PM</div>
                  <div className="text-2xl font-bold text-teal-600 mb-1">
                    £{tierInfo.normalRates.firstHour}/hr
                  </div>
                  <div className="text-xs text-gray-500">Per 30 min: £{tierInfo.normalRates.halfHour}</div>
                  <div className="text-xs text-teal-700 font-medium mt-2">
                    You earn 85%: £{(tierInfo.normalRates.firstHour * 0.85).toFixed(2)}/hr
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-orange-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-moon-line text-orange-600"></i>
                    <div className="text-xs text-gray-500">After Hours</div>
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-semibold rounded-full">+20%</span>
                  </div>
                  <div className="text-xs text-gray-500 mb-1">6:00 PM - 6:00 AM</div>
                  <div className="text-2xl font-bold text-orange-600 mb-1">
                    £{tierInfo.afterHoursRates.firstHour}/hr
                  </div>
                  <div className="text-xs text-gray-500">Per 30 min: £{tierInfo.afterHoursRates.halfHour}</div>
                  <div className="text-xs text-orange-700 font-medium mt-2">
                    You earn 85%: £{(tierInfo.afterHoursRates.firstHour * 0.85).toFixed(2)}/hr
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-alarm-warning-line text-purple-600"></i>
                    <div className="text-xs text-gray-500">Late Call-Out</div>
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-semibold rounded-full">+£{tierInfo.lateCalloutFee}</span>
                  </div>
                  <div className="text-xs text-gray-500 mb-1">After 8:00 PM</div>
                  <div className="text-2xl font-bold text-purple-600 mb-1">
                    +£{tierInfo.lateCalloutFee}
                  </div>
                  <div className="text-xs text-gray-500">Emergency fee</div>
                  <div className="text-xs text-purple-700 font-medium mt-2">
                    You earn 85%: +£{(tierInfo.lateCalloutFee * 0.85).toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 border border-teal-100">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <i className="ri-information-line text-teal-600"></i>
                  How You Get Paid
                </h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>You keep <strong>85%</strong> of all payments (15% platform fee)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>After-hours premium (+20%) applies from 6:00 PM to 6:00 AM</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>Late call-out fee (+£{tierInfo.lateCalloutFee}) for emergency jobs after 8:00 PM</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-teal-600 mt-0.5"></i>
                    <span>Payments processed automatically after job completion</span>
                  </li>
                </ul>
              </div>

              <div className="mt-4 p-3 bg-teal-100 rounded-lg">
                <p className="text-sm text-teal-800">
                  <i className="ri-information-line mr-2"></i>
                  You can add more service cities from your dashboard after completing registration.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Photo - Show after postcode verification */}
      {postcodeVerified && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Photo</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Your Photo *
              </label>
              <div className="flex items-start gap-4">
                {formData.profilePhoto && (
                  <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-teal-500">
                    <img
                      src={URL.createObjectURL(formData.profilePhoto)}
                      alt="Profile preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    id="profile-photo"
                  />
                  <label
                    htmlFor="profile-photo"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
                  >
                    <i className="ri-upload-2-line"></i>
                    {formData.profilePhoto ? 'Change Photo' : 'Choose Photo'}
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    JPG, PNG or GIF. Max size 5MB. This will be visible to clients.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {postcodeVerified && (
        <div className="flex justify-between items-center pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => window.history.back()}
            disabled={isSubmitting}
            className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting || !formData.profilePhoto}
            className="px-8 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploadingPhoto ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Uploading Photo...
              </>
            ) : isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Processing...
              </>
            ) : (
              <>
                Continue to Payment
                <i className="ri-arrow-right-line"></i>
              </>
            )}
          </button>
        </div>
      )}
    </form>
  );
}